import { TabList, Tab, makeStyles, tokens } from '@fluentui/react-components';

import { spacing } from '@/theme/tokens';
import type { ResourceTimeRange } from '@/models/resourceDetail';

interface TimeRangePickerProps {
  value: ResourceTimeRange;
  onChange: (next: ResourceTimeRange) => void;
}

const RANGES: { key: ResourceTimeRange; label: string }[] = [
  { key: '1h', label: '1 hour' },
  { key: '24h', label: '24 hours' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
];

const RANGE_KEYS: readonly ResourceTimeRange[] = RANGES.map((r) => r.key);

function isResourceTimeRange(value: unknown): value is ResourceTimeRange {
  return typeof value === 'string' && (RANGE_KEYS as readonly string[]).includes(value);
}

const useStyles = makeStyles({
  wrapper: {
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: `${spacing.xxs} ${spacing.xs}`,
    width: 'fit-content',
  },
});

export function TimeRangePicker({ value, onChange }: TimeRangePickerProps) {
  const classes = useStyles();
  return (
    <div className={classes.wrapper}>
      <TabList
        size="small"
        appearance="subtle"
        selectedValue={value}
        onTabSelect={(_, data) => {
          // Guard the cast — Fluent's `data.value` is typed as `unknown`, so
          // an unexpected tab id silently no-ops instead of corrupting the
          // selected range.
          if (isResourceTimeRange(data.value)) {
            onChange(data.value);
          }
        }}
        aria-label="Time range"
      >
        {RANGES.map((r) => (
          <Tab key={r.key} value={r.key}>
            {r.label}
          </Tab>
        ))}
      </TabList>
    </div>
  );
}
